import { useMemo, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { KeyRound, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useAuth } from '../../contexts/AuthProvider';
import type { User } from '../../contexts/AuthProvider';
import { mainMenu } from '../../shared/constants/menu';
import {
  canAccessRoles,
  collectMenuLinks,
  findActiveMenuLink,
  mergeMenuGroups,
} from '../../shared/utils/route-access';
import { MessagesBell } from '../../modules/messaging/MessagesBell';
import { ContextSwitcher } from '../../modules/auth/context/ContextSwitcher';
import type { MenuEntry } from '../../shared/constants/menu';
import { cn } from '@/components/ui/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NavMenuTooltip } from './navbar-tooltip';
import { MenuIcon } from './navbar-icons';
import { getMenuSubtitle } from './navbar-menu-meta';

interface NavbarComponentProps {
  expanded?: boolean;
  pinnedExpanded?: boolean;
  onToggleCollapse?: () => void;
  onLogout?: () => void;
}

/** ФИО «Фамилия Имя Отчество» из данных пользователя. */
function getUserFullName(user: User | null): string {
  const fio = [user?.family_name, user?.given_name, user?.middle_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fio || user?.login || user?.email || 'Пользователь';
}

/** Инициалы для кружка: из ФИО (Фамилия+Имя), иначе из email. */
function getUserInitials(user: User | null): string {
  const family = user?.family_name?.trim();
  const given = user?.given_name?.trim();
  if (family || given) {
    return `${family?.[0] ?? ''}${given?.[0] ?? ''}`.toUpperCase() || 'ЦД';
  }
  if (user?.email) {
    return user.email.slice(0, 2).toUpperCase();
  }
  return 'ЦД';
}

function NavMenuLinkItem({
  label,
  link,
  active,
  expanded,
}: {
  label: string;
  link: string;
  active: boolean;
  expanded: boolean;
}) {
  const subtitle = getMenuSubtitle(link, label);

  const linkNode = (
    <NavLink
      to={link}
      // Подсветку вложенных путей считает activeLink (самое длинное совпадение),
      // собственное сопоставление NavLink оставляем строгим, иначе оно вернёт
      // «активен» любому пункту, чей адрес является префиксом текущего.
      end
      className={({ isActive }) => {
        const isOn = active || isActive;
        return cn(
          'app-sidebar-nav-link group relative flex cursor-pointer transition-all duration-500',
          expanded ? 'items-start overflow-visible hover:shadow-lg' : 'items-center overflow-hidden hover:shadow-lg',
          expanded
            ? 'w-full gap-3 px-4 py-3.5 justify-start rounded-xl'
            : 'w-12 h-12 justify-center mx-auto rounded-full',
          isOn && 'app-sidebar-nav-link--active scale-105',
        );
      }}
      aria-label={label}
    >
      {({ isActive }) => {
        const isOn = active || isActive;
        return (
          <>
            <MenuIcon
              link={link}
              label={label}
              className={cn(
                'app-sidebar-nav-icon h-5 w-5 shrink-0 transition-colors duration-500',
                expanded && 'mt-0.5',
              )}
            />
            <div
              className={cn(
                'flex min-w-0 flex-1 flex-col transition-all duration-500 ease-in-out',
                expanded ? 'opacity-100' : 'max-w-0 overflow-hidden opacity-0',
              )}
            >
              <span
                className={cn(
                  'block text-sm font-medium',
                  expanded && 'leading-snug break-words whitespace-normal [overflow-wrap:anywhere]',
                )}
              >
                {label}
              </span>
              {subtitle && isOn && expanded && (
                <span className="mt-1 block text-xs leading-snug break-words whitespace-normal opacity-70 [overflow-wrap:anywhere]">
                  {subtitle}
                </span>
              )}
            </div>
            {isOn && !expanded && (
              <>
                <span className="app-sidebar-active-dot pointer-events-none absolute inset-0 animate-pulse rounded-full opacity-20" />
                <span className="app-sidebar-active-rail absolute -right-2 top-1/2 h-6 w-1 -translate-y-1/2 rounded-l-full" />
              </>
            )}
            {isOn && expanded && (
              <span className="app-sidebar-active-dot absolute right-2 top-4 h-2 w-2 animate-pulse rounded-full" />
            )}
          </>
        );
      }}
    </NavLink>
  );

  return (
    <div className="px-0.5">
      <NavMenuTooltip label={label} subtitle={subtitle} disabled={expanded}>
        {linkNode}
      </NavMenuTooltip>
    </div>
  );
}

function SidebarFooter({
  expanded,
  user,
  onLogout,
}: {
  expanded: boolean;
  user: User | null;
  onLogout?: () => void;
}) {
  if (!user) return null;

  const fullName = getUserFullName(user);
  const subtitle = user.counterparty?.name || 'Депозитарный учёт';

  const avatar = (
    <div
      className={cn(
        'app-sidebar-avatar w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 text-lg',
        onLogout && 'hover:scale-110 cursor-pointer',
      )}
    >
      {getUserInitials(user)}
    </div>
  );

  const avatarControl = onLogout ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        {avatar}
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" className="min-w-[12rem]">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm">{fullName}</div>
          {user.counterparty?.name && (
            <div className="text-xs text-muted-foreground">{user.counterparty.name}</div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Пароль есть не у всех: вход по ЭЦП и ЕСИ беспарольный. Пункт
            показываем всем — экран сам скажет, что менять нечего, а лишний
            запрос состояния пароля ради скрытия пункта того не стоит. */}
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/password">
            <KeyRound className="w-4 h-4" />
            Сменить пароль
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" className="cursor-pointer" onClick={onLogout}>
          <LogOut className="w-4 h-4" />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    avatar
  );

  return (
    <div className="app-sidebar-border px-5 py-5 flex flex-col items-center gap-4 border-t">
      {expanded && <ContextSwitcher enabled={!!user} />}
      <MessagesBell enabled={!!user} />
      {expanded && (
        <div className="text-center transition-all duration-500">
          <p className="app-sidebar-text font-medium text-sm">{fullName}</p>
          <p className="app-sidebar-text-muted text-xs">{subtitle}</p>
        </div>
      )}
      {!expanded && onLogout ? (
        avatarControl
      ) : !expanded ? (
        <NavMenuTooltip label={fullName} subtitle={subtitle}>
          {avatar}
        </NavMenuTooltip>
      ) : (
        avatarControl
      )}
    </div>
  );
}

export default function NavbarComponent({
  expanded = true,
  pinnedExpanded = false,
  onToggleCollapse,
  onLogout,
}: NavbarComponentProps) {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  // Доступ к меню — по ролям (модель привилегий отключена).
  // Та же функция, что гейтит маршруты в RoleRoute: меню и гард не расходятся.
  const hasRoleAccess = (roles?: string[]) => {
    if (!roles) return true;
    if (!isAuthenticated || !user) return false;
    return canAccessRoles(user, roles);
  };

  const menuLinks = useMemo(() => collectMenuLinks(mainMenu), []);

  // Подсвечивается пункт с самым длинным совпадением пути (см. findActiveMenuLink).
  const activeLink = useMemo(
    () => findActiveMenuLink(location.pathname, menuLinks),
    [menuLinks, location.pathname],
  );

  const isLinkActive = (link?: string) => {
    if (!link) return false;
    if (link === '/' || link === '/home') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    return link === activeLink;
  };

  const renderNavLink = (label: string, link: string, active: boolean, key: string) => (
    <NavMenuLinkItem key={key} label={label} link={link} active={active} expanded={expanded} />
  );

  /**
   * Группы заданы по ролям, поэтому у пользователя с несколькими ролями (и у
   * суперпользователя) одинаковые заголовки шли подряд: три «Справочника», два
   * «Основные», два «Отчёта». Сначала отбрасываем недоступное, потом склеиваем
   * одноимённые группы — по одному разделу на заголовок.
   */
  const visibleMenu = useMemo(() => {
    const visible = mainMenu.filter((entry) => {
      if ('type' in entry && entry.type === 'divider') return true;
      const item = entry as Exclude<MenuEntry, { type: 'divider' }>;
      if (!isAuthenticated && !item.forUnauthorized) return false;
      if (isAuthenticated && item.forUnauthorized) return false;
      return hasRoleAccess(item.roles);
    });
    return mergeMenuGroups(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const renderMenuItems = (items: MenuEntry[], level = 0) => {
    const nodes: ReactNode[] = [];

    items.forEach((item, index) => {
      if ('type' in item && item.type === 'divider') {
        if (expanded) {
          nodes.push(<hr key={`divider-${index}`} className="app-sidebar-border my-4 mx-1 border-t" />);
        }
        return;
      }

      const menuItem = item as Exclude<MenuEntry, { type: 'divider' }>;

      if (!isAuthenticated && !menuItem.forUnauthorized) return;
      if (isAuthenticated && menuItem.forUnauthorized) return;
      if (!hasRoleAccess(menuItem.roles)) return;

      const isParent = !!menuItem.children && menuItem.children.length > 0;
      const active = isLinkActive(menuItem.link);

      if (!expanded && isParent) {
        const subtitle = getMenuSubtitle(menuItem.link, menuItem.label);
        nodes.push(
          <NavMenuTooltip key={`${menuItem.label}-${index}`} label={menuItem.label} subtitle={subtitle}>
            <div
              className="app-sidebar-nav-link w-12 h-12 mx-auto rounded-full flex items-center justify-center"
              aria-hidden
            >
              <MenuIcon link={menuItem.link} label={menuItem.label} />
            </div>
          </NavMenuTooltip>,
        );
        return;
      }

      if (isParent && expanded) {
        nodes.push(
          <Accordion key={`${menuItem.label}-${index}`} type="multiple" className="w-full mb-2">
            <AccordionItem value={menuItem.label} className="app-sidebar-border border-none">
              <AccordionTrigger className="app-sidebar-section-title cursor-pointer px-1 py-3 text-left text-xs font-semibold uppercase tracking-wider break-words whitespace-normal leading-snug hover:no-underline [overflow-wrap:anywhere] [&>svg]:app-sidebar-section-chevron">
                {menuItem.label}
              </AccordionTrigger>
              <AccordionContent className="pb-2 pt-1">
                <div className={cn('space-y-3 px-0.5', level > 0 && 'pl-2')}>
                  {renderMenuItems(menuItem.children!, level + 1)}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>,
        );
        return;
      }

      if (!menuItem.link) return;

      nodes.push(renderNavLink(menuItem.label, menuItem.link, active, `${menuItem.label}-${index}`));
    });

    return nodes;
  };

  return (
    <div className="h-full py-6 pl-6 pr-3">
      <div
        className={cn(
          'app-sidebar-panel flex flex-col h-full transition-all duration-500 ease-in-out rounded-3xl shadow-2xl',
          expanded ? 'w-64' : 'w-20',
        )}
      >
        <div className="px-6 pt-6 pb-4 flex flex-col items-center relative min-h-[7.5rem] shrink-0">
          {onToggleCollapse && expanded && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="app-sidebar-pin-btn absolute top-3 right-3 flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-md transition-colors focus-visible:ring-[3px] focus-visible:ring-[#e5b4c0]/50"
              title={pinnedExpanded ? 'Режим по наведению' : 'Закрепить развёрнутым'}
              aria-label={pinnedExpanded ? 'Режим по наведению' : 'Закрепить развёрнутым'}
            >
              {pinnedExpanded ? (
                <PanelLeftClose className="w-4 h-4" />
              ) : (
                <PanelLeftOpen className="w-4 h-4" />
              )}
            </button>
          )}
          <div className="app-sidebar-brand-icon flex h-12 w-12 items-center justify-center rounded-full shadow-lg overflow-hidden bg-white">
            <img src="/logo-cd.svg" alt="Логотип Центрального депозитария" className="h-9 w-9 object-contain" />
          </div>
          <div
            className={cn(
              'mt-3 text-center transition-all duration-500 ease-in-out',
              expanded ? 'opacity-100' : 'opacity-0 pointer-events-none h-0 overflow-hidden',
            )}
          >
            <h2 className="app-sidebar-title text-lg whitespace-nowrap">СДУ</h2>
          </div>
        </div>

        <nav className="navbar-sidebar-scroll flex-1 min-h-0 overflow-x-hidden overflow-y-auto px-4 py-4">
          <div className="space-y-4 pb-2">{renderMenuItems(visibleMenu)}</div>
        </nav>

        {isAuthenticated && <SidebarFooter expanded={expanded} user={user} onLogout={onLogout} />}
      </div>
    </div>
  );
}
